import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMap } from 'react-map-gl/mapbox';
import { ButtonUI, LoadingSkeleton, TaptapList, TextWithTooltip } from 'sonata-react-components';
import { normalizeCountryToCca3 } from 'sonata-static-storage/lib/helpers/countries.helpers';

import { usePOIImporter } from '@/components/common/POIImporter/POIImporter.context';
import { useListGrouping } from '../../../grouping/hooks/useListGrouping';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { getCountryLabel, getVenueAttribution } from '../../../MultiFlightMap.helpers';
import { AttributionAndGroup } from '../common/AttributionAndGroup';
import { Title } from '../common/Title';
import { useListFilterPanel } from '../Filters/useListFilterPanel';
import ListsHeader from '../ListsHeader/ListsHeader';

import type { Venue } from 'sonata-static-storage/lib/schemas/venue/venue.schema';
import type { GroupView } from '../../../grouping/domain/grouping.types';

import '../lists.scss';

type Props = {
  mapId: string;
};

type State = {
  searchText: string[];
  titlesById: Record<string, string>;
};

type TaptapListItem = {
  id: string;
  groupIds: string[];
  groups: GroupView[];
  subtitle: string;
  title: string;
  notEditable: boolean;
  status: 'default' | 'highlighted' | 'notVisible';
  venue: Partial<Venue>;
  attribution: {
    active: boolean;
    enabled: boolean;
  };
};

function VenuesList({ mapId }: Props) {
  const { formatMessage } = useIntl();
  const { [mapId]: map } = useMap();

  const {
    activeSubsection,
    attributionZones,
    loadingVenuesList,
    selectedItemId,
    selectedItems: { venues: selectedIds },
    setSelectedItemId,
    setSelectedItems,
    temporaryVenues,
    updateVenue
  } = useMultiFlightMap();
  const {
    clearGroupFilter,
    deleteItems,
    editGroup,
    groupFilter,
    groupsOf,
    hasActiveGroupFilter,
    isHighlighted,
    matchesGroupFilter
  } = useListGrouping('venues');

  const [{ searchText, titlesById }, setState] = useState<State>({
    searchText: [],
    titlesById: {}
  });

  const { setAttributionMode, setShowModal } = usePOIImporter();

  const isProximityPointsSubsection = activeSubsection === 'proximityPoints';

  const handleAddButtonClick = () => {
    setAttributionMode(isProximityPointsSubsection ? 'proximityPoints' : 'attributionPOI');
    setShowModal(true);
  };

  const venuesList = useMemo(() => temporaryVenues?.filter(venue => !venue.deleted), [temporaryVenues]);

  const { matches: matchesLocation, panel: filterPanel } = useListFilterPanel('venues', venuesList, {
    activeCount: hasActiveGroupFilter ? 1 : 0,
    filters: [groupFilter],
    hasActive: hasActiveGroupFilter,
    onClear: clearGroupFilter
  });

  const handleSearchChange = (text: string) => {
    const searchValues = text.length ? text.split(',').map((a: string) => a.trim()) : [];
    setState(prevState => ({
      ...prevState,
      searchText: searchValues
    }));
  };

  const handleDeleteChange = (id: string) => {
    deleteItems([id]);
  };

  const handleDeleteAllChange = () => {
    deleteItems(selectedIds);
  };

  const handleCenterMapOnPoint = (venue: Partial<Venue>) => {
    setSelectedItemId(venue._id?.toString() || null);
    if (map && venue.latlng) {
      map.flyTo({
        center: [venue.latlng?.lng, venue.latlng?.lat],
        curve: 0.8,
        speed: 3,
        zoom: 16
      });
    }
  };

  const getText1 = (venue: Partial<Venue>) => {
    switch (venue.sourceType) {
      case 'myVenues':
        return formatMessage({
          defaultMessage: 'My Venues',
          id: `multiFlightListsMap.venues.list.text1.${venue.sourceType}`
        });
      case 'poiBrands':
        return formatMessage({
          defaultMessage: 'POI Brands',
          id: `multiFlightListsMap.venues.list.text1.${venue.sourceType}`
        });
      case 'custom':
        return formatMessage({
          defaultMessage: 'Upload file',
          id: `multiFlightListsMap.venues.list.text1.${venue.sourceType}`
        });
      case 'osm':
        return formatMessage({
          defaultMessage: 'OSM',
          id: `multiFlightListsMap.venues.list.text1.${venue.sourceType}`
        });
      case 'poi':
        return formatMessage({
          defaultMessage: 'POI',
          id: `multiFlightListsMap.venues.list.text1.${venue.sourceType}`
        });
      default:
        return formatMessage({
          defaultMessage: 'POI',
          id: 'multiFlightListsMap.venues.list.text1.poi'
        });
    }
  };

  const handleTitleChange = (value: string, id?: any) => {
    if (!id) {
      return;
    }
    setState(prevState => ({
      ...prevState,
      titlesById: {
        ...prevState.titlesById,
        [id]: value
      }
    }));
    const venueToUpdate = venuesList.find(venue => venue._id === id);
    if (venueToUpdate) {
      updateVenue({
        ...venueToUpdate,
        name: value
      });
    }
  };

  const handleSelectionChange = (selectedRows: { id: string }[]) => {
    setSelectedItems(
      'venues',
      selectedRows.map(row => row.id)
    );
  };

  const data: TaptapListItem[] = useMemo(() => {
    if (venuesList.length === 0) {
      return [];
    }
    return venuesList.map(venue => ({
      attribution: getVenueAttribution(venue._id?.toString() || '', attributionZones),
      groupIds: venue?.groups?.map(String) ?? [],
      groups: groupsOf(venue?.groups),
      id: venue._id!.toString(),
      notEditable: false,
      status:
        selectedItemId === venue._id?.toString() || isHighlighted(venue._id!.toString()) ? 'highlighted' : 'default',
      subtitle: [getText1(venue), getCountryLabel(venue.country ? normalizeCountryToCca3(venue.country) : '')].join(
        ' - '
      ),
      title: venue.name || 'My new venue',
      venue
    }));
  }, [venuesList, selectedItemId, attributionZones, groupsOf, isHighlighted]);

  const formattedData: TaptapListItem[] = useMemo(() => {
    const dataWithEditedName = data.map(item => ({
      ...item,
      title: titlesById[item.id!.toString()] || item.title
    }));

    const filteredBySearch = (() => {
      if (searchText.length === 0) {
        return dataWithEditedName;
      }
      const lowercaseText = searchText.map(s => s.toLowerCase());
      return dataWithEditedName.filter(({ title, id, subtitle, groups }) => {
        const groupNames = groups.map(group => group.name);
        return lowercaseText.some(search =>
          [title, id, subtitle, ...groupNames].join(' ').toLowerCase().includes(search)
        );
      });
    })();

    return filteredBySearch.filter(item => matchesGroupFilter(item.groupIds) && matchesLocation(item.venue));
  }, [data, titlesById, searchText, matchesGroupFilter, matchesLocation]);

  const defaultSelectedItems = useMemo(
    () => formattedData.filter(item => selectedIds.includes(item.id)),
    [formattedData, selectedIds]
  );

  const emptyStateTitle = venuesList.length
    ? formatMessage(
        activeSubsection === 'attributionPOIs'
          ? { defaultMessage: 'No attribution POIs', id: 'multiFlightListsMap.venues.list.emptyState.title' }
          : {
              defaultMessage: 'No proximity points',
              id: 'multiFlightListsMap.venues.list.emptyState.proximityPoints.title'
            }
      )
    : formatMessage({ defaultMessage: 'Ready to get started?', id: 'multiFlightListsMap.emptyState.readyToStart' });

  const emptyStateDescription = venuesList.length
    ? formatMessage({
        defaultMessage: 'No venues match the filters',
        id: 'multiFlightListsMap.venues.list.emptyState.noMatches'
      })
    : formatMessage(
        isProximityPointsSubsection
          ? {
              defaultMessage: 'Add your first Proximity Point clicking below.',
              id: 'multiFlightListsMap.venues.list.emptyState.proximityPoints.getStarted'
            }
          : {
              defaultMessage: 'Add your first Attribution POI clicking below.',
              id: 'multiFlightListsMap.venues.list.emptyState.getStarted'
            }
      );

  if (loadingVenuesList) {
    return <LoadingSkeleton count={10} height={62} />;
  }

  return (
    <div className="mapList">
      <ListsHeader
        actionButtonsId={`actionButtons-venuesList-${mapId}`}
        filtersId={`filters-venuesList-${mapId}`}
        panel={filterPanel}
        search={{
          id: `venuesList-textFilter-${mapId}`,
          name: 'venuesList-textFilter',
          onChange: handleSearchChange,
          placeholder: formatMessage({
            defaultMessage: 'Search by name, ID...',
            id: 'multiFlightListsMap.search.locations'
          })
        }}
      />
      <div className="mapList__listContainer">
        <TaptapList
          className="mapList__list"
          data={formattedData}
          defaultSelectedItems={defaultSelectedItems}
          id={`venuesList-${mapId}`}
          onSelectionChange={items => handleSelectionChange(items as { id: string }[])}
          selectionType="rowClick"
        >
          {venuesList.length > 0 && (
            <TaptapList.Toolbar>
              <TaptapList.Toolbar.SelectionSummary />
              <TaptapList.Toolbar.SelectAll />
              <div className="toolbarDeleteAll">
                <ButtonUI
                  disabled={selectedIds.length === 0}
                  icon="fa-regular fa-trash"
                  id={`deleteSelectedAction-${mapId}`}
                  onClick={handleDeleteAllChange}
                  shape="link"
                  size="ultraSmall"
                  text={formatMessage({
                    defaultMessage: 'Delete',
                    id: 'multiFlightListsMap.emptyState.locations.delete'
                  })}
                  type="secondary"
                />
              </div>
            </TaptapList.Toolbar>
          )}
          <TaptapList.RowsListVirtualized<TaptapListItem>
            emptyStateDescription={emptyStateDescription}
            emptyStateIcon="fa-solid fa-empty-set"
            emptyStateTitle={emptyStateTitle}
            focusItemId={selectedItemId}
          >
            {listItem =>
              listItem.map(item => (
                <TaptapList.RowsListVirtualized.Row id={item.id} key={item.id} virtualData={item.virtualData}>
                  <Title
                    className="mapList__listRowsListRowItemTitle"
                    notEditable={item.notEditable}
                    onTextChange={(newValue: string) => handleTitleChange(newValue, item.id)}
                    rowId={item.id}
                    text={item.title}
                  />
                  <div className="mapList__listRowsListRowItemActions">
                    <ButtonUI
                      className="mapList__listRowsListRowDeleteButton"
                      icon="fa solid fa-xmark"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleDeleteChange(item.id);
                      }}
                      shape="link"
                      size="ultraSmall"
                      type="secondary"
                    />
                  </div>
                  <div className="mapList__listRowsListRowItemSubtitle">
                    <ButtonUI
                      className="mapList__listRowsListRowPinButton"
                      icon="fa-solid fa-map-pin"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleCenterMapOnPoint(item.venue as Venue);
                      }}
                      shape="link"
                      size="ultraSmall"
                      type="secondary"
                    />
                    <TextWithTooltip text={item.subtitle} />
                  </div>
                  <AttributionAndGroup
                    attribution={item.attribution}
                    groupMenu={{ family: 'venues', groups: item.groups, onEditGroup: editGroup }}
                    venueId={item.id}
                  />
                </TaptapList.RowsListVirtualized.Row>
              ))
            }
          </TaptapList.RowsListVirtualized>
        </TaptapList>
      </div>
      <div className="mapList__addPOIContainer">
        <ButtonUI
          className="mapList__addPOIButton"
          icon="fa-solid fa-plus"
          onClick={handleAddButtonClick}
          text={
            isProximityPointsSubsection
              ? formatMessage({
                  defaultMessage: 'Add Proximity Points',
                  id: 'proximityPoints.addButton'
                })
              : formatMessage({
                  defaultMessage: 'Add Attribution POI',
                  id: 'attributionPOI.addButton'
                })
          }
          type="secondary"
        />
      </div>
    </div>
  );
}

export default VenuesList;
